import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
// Hand-rolled menu that reuses the shared component's classes without mounting it,
// so it has to pull the stylesheet in itself.
import '../../shared/components/ActionsMenu.css';
import { isProtectedTeamOwner } from '../utils/teamFilterUtils';

const deleteModalContentStyle = {
  textAlign: 'left',
  border: 'none',
  borderRadius: '12px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
  width: '420px',
  padding: '0'
};

const TeamActionsMenu = ({
  teamManageable,
  onEdit,
  onDelete,
  onResetPassword,
  csrfToken,
  team,
  openUpwards = false
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEdit = () => {
    setMenuOpen(false);
    onEdit(teamManageable);
  };

  const handleCloseDeleteConfirm = () => {
    setConfirmOpen(false);
  };

  const handleDeleteClick = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    setConfirmOpen(true);
  };

  const handleResetPassword = () => {
    setMenuOpen(false);
    onResetPassword(csrfToken, teamManageable, team);
  };

  const canResetPassword = teamManageable.type_for_ui === 'TeamMembership' &&
    teamManageable.user &&
    teamManageable.user.id;

  const canDelete = !isProtectedTeamOwner(teamManageable, team);

  return (
    <div className="team-actions-menu" ref={menuRef}>
      <button
        className="team-actions-btn"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Actions"
      >
        <i className="fas fa-ellipsis-v" style={{ fontSize: '16px' }}></i>
      </button>

      {menuOpen && (
        <div className={`team-actions-dropdown${openUpwards ? ' team-actions-dropdown--up' : ''}`}>
          <button className="team-actions-item" onClick={handleEdit}>
            <i className="fal fa-pencil" style={{ fontSize: '14px', width: '16px' }}></i>
            <span>Edit</span>
          </button>

          {canResetPassword && (
            <button className="team-actions-item" onClick={handleResetPassword}>
              <i className="fal fa-key" style={{ fontSize: '14px', width: '16px' }}></i>
              <span>Reset Password</span>
            </button>
          )}

          {canDelete && (
            <button
              className="team-actions-item team-actions-item-danger"
              onClick={handleDeleteClick}
            >
              <i className="fal fa-trash-alt" style={{ fontSize: '14px', width: '16px' }}></i>
              <span>Delete</span>
            </button>
          )}
        </div>
      )}

      {confirmOpen && (
        <div className="team-modal-overlay">
          <div className="team-modal" style={deleteModalContentStyle}>
            <div className="team-modal-header">
              <h3 className="team-modal-title">Delete team member?</h3>
              <button
                className="team-modal-close"
                onClick={handleCloseDeleteConfirm}
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
            <div className="team-modal-body">
              <p style={{ marginBottom: '8px' }}>
                You're about to delete team member <strong>{teamManageable.name}</strong>.
              </p>
              <p style={{ marginBottom: '8px' }}>This cannot be undone.</p>
              <p>Are you sure?</p>
            </div>
            <div className="team-modal-footer">
              <button
                className="team-btn-secondary"
                onClick={handleCloseDeleteConfirm}
              >
                Cancel
              </button>
              <button
                className="team-btn-danger"
                onClick={(e) => {
                  e.preventDefault();
                  onDelete(csrfToken, team, teamManageable, handleCloseDeleteConfirm);
                }}
              >
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

TeamActionsMenu.propTypes = {
  teamManageable: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onResetPassword: PropTypes.func.isRequired,
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  openUpwards: PropTypes.bool
};

export default TeamActionsMenu;
